"use client";

import { useState } from "react";
import { HexColorPicker } from "react-colorful";
import { Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@workspace/ui/components/dropdown-menu";
import {
  buildColorSuggestions,
  type ColorSuggestion,
} from "@/lib/theme/color-suggestions";

/**
 * Compact colour picker: a swatch that opens a popover holding the saturation
 * area, a hex field, and one-click shortcuts to colours already used elsewhere
 * in the theme.
 *
 * A simplified take on the `apps/css-helper` theme-editor picker — same
 * `react-colorful` core and swatch-plus-popover shape, minus the alpha channel
 * and multi-format cycling. Document themes have no use for transparency (a
 * translucent body ink over a themed page is a bug, not a feature), and the
 * stored format is settled by `sanitizeOverrides`, so a format switcher would
 * expose a choice that cannot round-trip.
 */
export function ColorPicker({
  value,
  onChange,
  label,
  suggestions,
}: {
  /** Current colour, as `#rrggbb`. */
  value: string;
  onChange: (hex: string) => void;
  /** Names the control for assistive tech, and identifies this row's own
   *  palette entry so it is not offered back to itself, e.g. "Accent". */
  label: string;
  /** Other colours in the theme; deduped and offered inside the popover. */
  suggestions?: ColorSuggestion[];
}) {
  const [open, setOpen] = useState(false);
  // Holds an in-progress hex edit only. `null` means "show `value`", so a
  // change from outside (preset switch, suggestion click, reset) needs no
  // syncing effect — the field is derived until the user types into it.
  const [typed, setTyped] = useState<string | null>(null);
  const draft = typed ?? value;

  const chips = suggestions
    ? buildColorSuggestions(suggestions, value, label)
    : [];

  const commit = (next: string) => {
    setTyped(null);
    onChange(next);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`${label} colour, ${value}`}
          className="h-7 w-9 shrink-0 cursor-pointer rounded border border-border p-0.5 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
        >
          <span
            className="block h-full w-full rounded-sm"
            style={{ backgroundColor: value }}
          />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-auto p-3">
        {/* Sized inline rather than by class: react-colorful sets its own
            width/height in injected CSS, and 168px keeps the popover inside the
            384px sidebar with room for the suggestion rows. */}
        <HexColorPicker
          color={value}
          onChange={commit}
          style={{ width: 168, height: 140 }}
        />

        <input
          value={draft}
          onChange={(e) => {
            const next = e.target.value;
            setTyped(next);
            // Only propagate once it is a complete colour, so typing "#9" does
            // not repaint the document with a partial value.
            if (/^#[0-9a-f]{6}$/i.test(next)) onChange(next.toLowerCase());
          }}
          // Drop a half-finished edit so the field falls back to the real value.
          onBlur={() => setTyped(null)}
          spellCheck={false}
          aria-label={`${label} hex value`}
          className="mt-2 w-full rounded border border-border bg-muted px-2 py-1 font-mono text-xs outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
        />

        {chips.length > 0 && (
          <>
            <div className="mt-3 mb-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              Use existing
            </div>
            <div className="flex flex-col gap-0.5">
              {chips.map((c) => (
                <button
                  key={c.hex}
                  type="button"
                  // Already the current value: shown so the user can see the two
                  // are tied, but there is nothing to apply, so it is inert.
                  disabled={c.current}
                  onClick={() => {
                    commit(c.hex);
                    setOpen(false);
                  }}
                  aria-label={
                    c.current
                      ? `Already using ${c.label.toLowerCase()} colour ${c.hex}`
                      : `Use ${c.label.toLowerCase()} colour ${c.hex}`
                  }
                  className="flex items-center gap-2 rounded px-1 py-1 text-left text-xs enabled:hover:bg-accent disabled:cursor-default"
                >
                  <span
                    className="h-3.5 w-3.5 shrink-0 rounded-sm border border-border"
                    style={{ backgroundColor: c.hex }}
                  />
                  <span className="flex-1">{c.label}</span>
                  <span className="font-mono text-[10px] text-muted-foreground">
                    {c.hex}
                  </span>
                  {/* Reserve the tick's width on every row so the hex column
                      does not shift as the match moves between rows. */}
                  <span className="w-3 shrink-0">
                    {c.current && <Check className="h-3 w-3" />}
                  </span>
                </button>
              ))}
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
